import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Star, Users, Clock, Award, ChevronRight, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WACA_LOGO = "https://static.prod-images.emergentagent.com/jobs/cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/images/3be115c4bea55831ae8e7a94864fbe6ccfd9d215db4a9237d5ba56321562dcf3.jpeg";
const N369_SEAL = "https://static.prod-images.emergentagent.com/jobs/cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/images/1fca156725e4149dee6895773a290d321ce04b2222a7b6d468f342a9ac1534f8.jpeg";

// Alliance Banner shown at the top of the catalog hero
const AllianceBanner = () => (
  <div className="w-full bg-white/10 border-b border-white/15 py-4 px-4">
    <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
      {/* Left: WACA by Neuralia369 */}
      <div className="flex items-center gap-4 group">
        <img src={WACA_LOGO} alt="WACA - World Affairs Council of Austin"
          className="w-14 h-14 rounded-2xl object-cover border-2 border-amber-400/60 shadow-xl" />
        <div>
          <p className="text-white font-black text-base leading-tight">WACA</p>
          <p className="text-amber-300 text-xs font-medium">World Affairs Council of Austin</p>
          <p className="text-white/50 text-[10px] mt-0.5">by Neuralia369</p>
        </div>
      </div>
      {/* Center */}
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-px bg-white/30" />
          <div className="border border-white/20 rounded-full px-4 py-1.5 bg-white/5">
            <span className="text-xs tracking-widest uppercase font-bold text-white/80">Alianza Académica</span>
          </div>
          <div className="w-10 h-px bg-white/30" />
        </div>
        <p className="text-white/40 text-[10px] tracking-wide">LatAm · Formación en IA</p>
      </div>
      {/* Right: Neuralia369 by WACA */}
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="text-white font-black text-base leading-tight">Neuralia369</p>
          <p className="text-indigo-300 text-xs font-medium">Academy</p>
          <p className="text-white/50 text-[10px] mt-0.5">by WACA</p>
        </div>
        <img src={N369_SEAL} alt="Neuralia369 Academy"
          className="w-14 h-14 rounded-2xl object-cover border-2 border-indigo-400/60 shadow-xl" />
      </div>
    </div>
  </div>
);


const LINE_COLORS = { kids:'from-purple-500 to-pink-500', teens:'from-blue-500 to-cyan-500', adultos:'from-emerald-500 to-teal-500', profesionales:'from-amber-500 to-orange-500', social:'from-rose-500 to-pink-600', mediadores:'from-violet-600 to-blue-600' };
const LINE_LABELS_MAP = { kids:'Para Niños', teens:'Para Jóvenes', adultos:'Para Adultos', profesionales:'Profesionales', social:'Impulso IA', mediadores:'Mediadores del Futuro' };
const ORIGIN_BADGE = { neuralia:'Neuralia369', waca:'con WACA', coproducido:'Coproducido' };

const AGE_FILTERS = [
  { label:'Todos', value:null },
  { label:'5-6 años', value:'kids-5', min:5, max:6 },
  { label:'7-9 años', value:'kids-7', min:7, max:9 },
  { label:'10-12 años', value:'kids-10', min:10, max:12 },
  { label:'13-17 años', value:'teens', min:13, max:17 },
  { label:'Adultos (18+)', value:'adultos', min:18, max:99 },
];

const CourseCard = ({ course }) => (
  <Link to={`/academy/${course.id}`} data-testid={`course-card-${course.id}`}
    className="group flex flex-col rounded-2xl border border-border-subtle bg-card/60 overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all duration-200">
    <div className={`h-36 bg-gradient-to-br ${LINE_COLORS[course.line] || 'from-primary/60 to-primary/20'} relative flex items-center justify-center`}>
      {course.nuri_mascot && (
        <div className="absolute top-3 right-3 rounded-full bg-white/20 px-2 py-1 text-[10px] font-bold text-white">Nuri</div>
      )}
      <span className="text-5xl">🎓</span>
      <div className="absolute bottom-3 left-3">
        <span className="rounded-full bg-black/30 px-2 py-1 text-[10px] text-white">{LINE_LABELS_MAP[course.line]}</span>
      </div>
    </div>
    <div className="flex flex-col flex-1 p-5 space-y-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] text-text-muted font-mono">{course.code}</span>
          {course.origin !== 'neuralia' && (
            <span className="text-[10px] text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5">{ORIGIN_BADGE[course.origin]}</span>
          )}
        </div>
        <h3 className="font-bold text-text-heading text-base leading-snug group-hover:text-primary transition">{course.name}</h3>
        <p className="text-xs text-text-muted mt-1 line-clamp-2">{course.description_short}</p>
      </div>
      <div className="flex items-center gap-4 text-xs text-text-muted">
        <span className="flex items-center gap-1"><Clock size={11} /> {course.weeks} sem.</span>
        <span className="flex items-center gap-1"><BookOpen size={11} /> {course.modules_count} módulos</span>
        {course.age_min && <span className="flex items-center gap-1"><Users size={11} /> {course.age_min}-{course.age_max} años</span>}
      </div>
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-border-subtle">
        <span className="font-bold text-lg text-primary">${course.price_usd} <span className="text-xs font-normal text-text-muted">USD</span></span>
        <span className="flex items-center gap-1 text-xs text-text-muted group-hover:text-primary transition">Ver curso <ChevronRight size={13} /></span>
      </div>
    </div>
  </Link>
);

export default function AcademyCatalogPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [lineFilter, setLineFilter] = useState(null);

  useEffect(() => {
    let url = `${API}/academy/courses`;
    const params = [];
    if (lineFilter) params.push(`line=${lineFilter}`);
    if (params.length) url += '?' + params.join('&');
    setLoading(true);
    fetch(url).then(r => r.json()).then(d => setCourses(d.courses || [])).finally(() => setLoading(false));
  }, [lineFilter]);

  const filtered = activeFilter
    ? courses.filter(c => c.age_min <= (activeFilter.max || 99) && c.age_max >= (activeFilter.min || 0))
    : courses;

  return (
    <div className="min-h-screen bg-background" data-testid="academy-catalog-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#3730a3] to-[#1e1b4b] text-white" data-testid="academy-hero">
        <div className="py-14 px-4">
        <div className="max-w-5xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm">
            <Award size={14} /> Academy · Formación en IA
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Neuralia369 Academy</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">Formación en Inteligencia Artificial para todas las edades. Desde 5 años hasta profesionales. Certificados verificables.</p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {[['13+', 'Cursos'], ['5+', 'Años desde'], ['100%', 'Online'], ['Certificado', 'Verificable']].map(([n, l]) => (
              <div key={l} className="rounded-2xl bg-white/10 px-5 py-3 text-center">
                <p className="text-xl font-black">{n}</p>
                <p className="text-xs text-white/70">{l}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
        {/* Alliance section — below stats, still in hero */}
        <AllianceBanner />
      </div>

      {/* Filtros */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border-subtle px-4 py-3">
        <div className="max-w-5xl mx-auto flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {AGE_FILTERS.map(f => (
            <button key={f.value || 'all'} onClick={() => setActiveFilter(f.value ? f : null)}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition ${activeFilter?.value === f.value || (!activeFilter && !f.value) ? 'bg-primary text-[#0A1A3F]' : 'border border-border-subtle text-text-muted hover:border-primary/40'}`}
              data-testid={`filter-${f.value || 'all'}`}>
              {f.label}
            </button>
          ))}
          <div className="w-px bg-border-subtle mx-2" />
          {Object.entries(LINE_LABELS_MAP).map(([k, v]) => (
            <button key={k} onClick={() => setLineFilter(lineFilter === k ? null : k)}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition ${lineFilter === k ? 'bg-primary text-[#0A1A3F]' : 'border border-border-subtle text-text-muted hover:border-primary/40'}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-card/40 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <p className="text-sm text-text-muted mb-6">{filtered.length} curso{filtered.length !== 1 ? 's' : ''} disponible{filtered.length !== 1 ? 's' : ''}</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(c => <CourseCard key={c.id} course={c} />)}
            </div>
          </>
        )}
      </div>

      {/* CTAs secundarios */}
      <div className="bg-card/30 border-t border-border-subtle py-12 px-4">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
          {[
            { title: 'Para familias', desc: 'Gestiona los perfiles de tus hijos y acompaña su aprendizaje', link: '/academy/para-familias', emoji: '👨‍👩‍👧‍👦' },
            { title: 'Para empresas', desc: 'Compra plazas para tu equipo e integra la formación en Fundae', link: '/academy/para-empresas', emoji: '🏢' },
            { title: 'Impulso IA', desc: 'Ruta social con cupos becados para jóvenes de 15 a 24 años', link: '/academy/impulso-ia', emoji: '🚀' },
          ].map(cta => (
            <Link key={cta.link} to={cta.link} className="glass-card p-5 hover:border-primary/40 transition rounded-2xl">
              <p className="text-3xl mb-2">{cta.emoji}</p>
              <p className="font-bold text-text-heading">{cta.title}</p>
              <p className="text-xs text-text-muted mt-1">{cta.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
